<template>
  <div class="dokumente-page">
    <div class="page-header">
      <h1 class="page-title">
        Vertragsdokumente
        <span v-if="tarif" class="project-red">
          {{ tarif.un_name }} {{ tarif.tarif_title }}
        </span>
      </h1>
      <p class="page-subtitle">
        Übersicht der hinterlegten Vertragsunterlagen (Downloads) für diesen Vertrag.
      </p>
    </div>

    <div v-if="isLoading" class="state-block" data-state="loading">
      <v-progress-circular
        indeterminate
        color="#EA5545"
        size="64"
      ></v-progress-circular>
      <p class="state-text">Dokumente werden geladen...</p>
    </div>

    <div
      v-else-if="!errorMessage && documents.length > 0"
      class="documents-section"
    >
      <v-card class="stats-card" elevation="1">
        <v-card-text>
          <div class="stats-header">
            <v-icon size="22" color="#EA5545" class="mr-2">mdi-file-download</v-icon>
            <div class="stats-title-block">
              <div class="stats-title">Vertragsdokumente</div>
              <div class="stats-subtitle">
                Es wurden {{ documents.length }} Dokumente für diesen Vertrag gefunden.
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <v-card
        v-for="doc in documents"
        :key="doc.vdoc_id"
        class="document-card"
        elevation="1"
      >
        <v-card-text class="document-content">
          <div class="document-main">
            <div class="document-title">
              <v-icon size="20" color="#EA5545" class="mr-2">mdi-file-pdf-box</v-icon>
              <span>{{ doc.vdoc_text || 'Vertragsdokument' }}</span>
            </div>
            <div class="document-meta">
              <span v-if="doc.vdoc_dateiname" class="document-filename">
                Datei: {{ doc.vdoc_dateiname }}
              </span>
            </div>
          </div>
          <div class="document-actions">
            <v-btn
              color="#EA5545"
              variant="outlined"
              size="small"
              :href="getDocumentUrl(doc.vdoc_id)"
              target="_blank"
            >
              <v-icon left size="18">mdi-open-in-new</v-icon>
              Dokument öffnen
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <div
      v-else-if="!isLoading && !errorMessage"
      class="state-block" data-state="empty"
    >
      <v-icon size="64" color="#ccc">mdi-file-document-outline</v-icon>
      <h3 class="state-title">Keine Vertragsdokumente gefunden</h3>
      <p class="state-text">
        Für diesen Vertrag sind aktuell keine Vertragsunterlagen hinterlegt.
      </p>
    </div>

    <div
      v-else-if="errorMessage"
      class="state-block" data-state="empty"
    >
      <v-icon size="64" color="error">mdi-alert-circle</v-icon>
      <h3 class="state-title">Fehler beim Laden der Dokumente</h3>
      <p class="state-text">
        {{ errorMessage }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { CLEARMENT_TITLE, CLEARMENT_DESCRIPTION } from '@/constants/documentHead'

const baseCalls = inject('baseCalls')
const errorStore = inject('errorStore')
const updateDocumentHead = inject('updateDocumentHead')
updateDocumentHead?.(CLEARMENT_TITLE, CLEARMENT_DESCRIPTION)

const route = useRoute()

const documents = ref([])
const tarif = ref(null)
const isLoading = ref(false)
const errorMessage = ref('')

const routeId = computed(() => {
  const rawId = route.params.id
  if (!rawId && rawId !== 0) return null
  const parsed = Number(rawId)
  return Number.isNaN(parsed) ? null : parsed
})

const hasRouteId = computed(() => routeId.value !== null)

function getDocumentUrl(vdocId) {
  if (!vdocId) return null
  return `/vdoc/${vdocId}`
}

async function loadDocuments() {
  if (!hasRouteId.value) {
    documents.value = []
    tarif.value = null
    errorMessage.value = 'Es wurde keine Vertrags-ID übergeben.'
    return
  }

  isLoading.value = true
  errorMessage.value = ''

  try {
    const response = await baseCalls.clearmentPost({
      action: 'getdocumentsformyproduct',
      myp_id: routeId.value
    })

    if (response.data.success) {
      documents.value = response.data.documents || []
      tarif.value = response.data.tarif || null
    } else {
      documents.value = []
      tarif.value = null
      errorMessage.value =
        response.data.message ||
        'Die Vertragsdokumente konnten nicht geladen werden.'
      if (errorStore && typeof errorStore.setError === 'function') {
        errorStore.setError(true, errorMessage.value)
      }
    }
  } catch (err) {
    console.error('Error loading documents:', err)
    documents.value = []
    tarif.value = null
    errorMessage.value = 'Beim Laden der Vertragsdokumente ist ein Fehler aufgetreten.'
    if (errorStore && typeof errorStore.setError === 'function') {
      errorStore.setError(true, errorMessage.value)
    }
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  document.title = 'Clearment | Vertragsdokumente'
  loadDocuments()
})

watch(
  () => route.params.id,
  () => {
    loadDocuments()
  }
)
</script>

<style scoped src="@/assets/styles/sfc/components/sites/DokumenteIndex.css"></style>
